/**
 * Procedural cloud generation: a layer of clouds as gaussian splats.
 *
 * Pure and deterministic: the same look, box, budget and seed give the same Float32Arrays on
 * every device, so every visitor sees the same sky.
 *
 * How a layer is built:
 * 1. Cloud centres ("anchors") on a jittered grid over the layer, one per cell, at the spacing the
 *    preset asks for. Each cloud gets a size (log-normal), a rank (coverage threshold, ordered by
 *    a low-frequency noise so clouds appear in coherent fields and streets as coverage rises) and
 *    a life phase (form / dissipate cycle).
 * 2. Each cloud is a density field:
 *    - cumulus family: a union of soft spheres (Dobashi-style metaballs). A few base puffs sit on
 *      a flat condensation level; billow generations stack smaller puffs on the upper hemisphere
 *      of their parents (Bouthors & Neyret's hierarchical cumulus surfaces), climbing into towers
 *      and an anvil for storms;
 *    - stratus / fog: a slab whose top and bottom heights follow fBm, so decks undulate and break;
 *    - cirrus: curved strands (mares' tails) that hook at the end.
 *    Billowy noise erodes each field's edges (the Perlin-Worley erosion of Schneider's Nubis), so
 *    rims are broken and wispy rather than spherical.
 * 3. Splats are rejection-sampled from the field: denser regions get more splats, the interior
 *    gets larger and more opaque ones, the rim smaller and fainter ones. Each splat's size is set
 *    from the local spacing between splats so neighbours overlap into a continuous volume.
 * 4. Every splat is also splatted into a low-resolution 3D extinction grid of the whole layer
 *    (periodic across the layer, like the clouds' wrap-around drift). The shader marches it
 *    toward the sun for each splat's self-shadowing (Harris & Lastra's per-particle light
 *    transmittance, done per frame on the GPU), and the column sums give the soft cloud-shadow
 *    map projected onto the scan.
 */
import { billow3, clamp, createRng, fbm3, gaussian, lerp, smoothstep, valueNoise3 } from './cloudNoise';
import type { CloudLook } from './cloudParams';

export interface CloudGenerationInput {
    look: CloudLook;
    /** World size of the layer box (dome: x and z are the diameter). */
    box: { x: number; y: number; z: number };
    /** Splat budget for the layer. */
    budget: number;
}

export interface CloudGeometry {
    count: number;
    /** Per splat (4 floats): cloud anchor u (−0.5..0.5 of box x), base y (−0.5..0.5 of box y), anchor v, cloud rank. */
    anchor: Float32Array;
    /** Per splat: offset from the cloud anchor (world units), splat size (gaussian sigma). */
    offset: Float32Array;
    /** Per splat: rotation quaternion x, y, z, w. */
    rotation: Float32Array;
    /** Per splat: axis scale x, y, z (times sigma), opacity. */
    shape: Float32Array;
    /**
     * Per splat: height in its cloud 0..1, rim 0 (core)..1 (edge), crease 0 (a fold between
     * billows, in shadow)..1 (a billow's lit lobe), cloud life phase.
     */
    extra: Float32Array;
    /**
     * RG8 extinction grid: R = extinction / sigmaMax, G = rank of the clouds there. One x-z slice
     * per height level (x fastest, then z, then y), uploaded as a 2D texture array.
     */
    grid: Uint8Array;
    gridDims: [number, number, number];
    /** Local y range (world units, box centre = 0) the grid spans. */
    gridY: [number, number];
    /** Extinction (per world unit) that R = 255 stands for. */
    sigmaMax: number;
    /** RG8 column map (x fastest, then z): R = optical depth / tauMax, G = rank. */
    shadow: Uint8Array;
    shadowDims: [number, number];
    tauMax: number;
    /** The box this geometry was generated for. */
    genBox: { x: number; y: number; z: number };
    /** How far splats reach beyond their anchors: sideways, above the box centre, below it. */
    extent: { side: number; top: number; bottom: number };
    clouds: number;
    /** World size of a grid cell (smallest axis): the shader's light-march step. */
    cellSize: number;
}

interface Puff {
    x: number;
    y: number;
    z: number;
    r: number;
    /** Vertical radius / r (ellipsoids for anvils and flat bases). */
    sy: number;
}

interface CloudPlan {
    u: number;
    v: number;
    baseY: number;
    width: number;
    height: number;
    rank: number;
    life: number;
    yaw: number;
    seed: number;
    budget: number;
}

const MAX_CLOUDS = 900;
/** Opacity cap: splats at or above the shadow system's caster opacity (0.6) would cast into maps. */
export const CLOUD_MAX_SPLAT_OPACITY = 0.55;

/** Generate a layer. */
export function generateCloudLayer(input: CloudGenerationInput): CloudGeometry {
    const { look } = input;
    const shape = look.shape;
    const box = {
        x: Math.max(1e-3, input.box.x),
        y: Math.max(1e-3, input.box.y),
        z: Math.max(1e-3, input.box.z),
    };
    const budget = Math.max(200, Math.floor(input.budget));
    const rng = createRng(look.seed * 7919 + 17);
    const seed = look.seed;
    const W = look.cloudScale;
    const dome = look.area === 'dome';

    // ── 1. cloud anchors ─────────────────────────────────────────────────────────────────
    const spacing = Math.max(W * shape.spacing, 1e-3);
    let nx = Math.max(1, Math.round(box.x / spacing));
    let nz = Math.max(1, Math.round(box.z / spacing));
    while (nx * nz > MAX_CLOUDS) {
        nx = Math.max(1, Math.floor(nx * 0.9));
        nz = Math.max(1, Math.floor(nz * 0.9));
    }
    const plans: CloudPlan[] = [];
    const jitter = shape.kind === 'stratus' || shape.kind === 'fog' ? 0.5 : 0.8;
    for (let iz = 0; iz < nz; iz++) {
        for (let ix = 0; ix < nx; ix++) {
            const u = (ix + 0.5 + (rng() - 0.5) * jitter) / nx - 0.5;
            const v = (iz + 0.5 + (rng() - 0.5) * jitter) / nz - 0.5;
            // dome: the layer is a disc; anchors in the corners never show
            if (dome && u * u + v * v > 0.25) {
                rng(); rng(); rng(); rng(); rng();
                continue;
            }
            const sizeFactor = clamp(Math.exp(gaussian(rng) * shape.sizeVariance), 0.45, 2.1);
            let width = W * sizeFactor;
            let height = width * shape.aspect * lerp(0.8, 1.2, rng());
            // A cloud never outgrows the layer's thickness (except fog, which is the thickness).
            const maxHeight = box.y * (shape.kind === 'cumulonimbus' ? 1.05 : 0.95);
            // fog fills the layer's thickness (its top is the layer's top, whatever the patch width)
            if (shape.kind === 'fog') height = box.y * lerp(0.6, 1.0, rng());
            else if (shape.kind === 'stratus') height = Math.min(height, box.y * lerp(0.6, 1.0, rng()));
            else height = Math.min(height, maxHeight);
            if (shape.kind === 'cirrus') width *= lerp(0.8, 1.4, rng());
            // Base heights: cumulus share a condensation level near the bottom of the layer,
            // cirrus float anywhere in it, fog sits on its floor.
            let baseY: number;
            if (shape.kind === 'cirrus') baseY = -0.5 * box.y + rng() * Math.max(0, box.y - height);
            else if (shape.kind === 'fog') baseY = -0.5 * box.y;
            else if (shape.kind === 'stratus') baseY = -0.5 * box.y + rng() * Math.max(0, box.y - height) * 0.35;
            else baseY = -0.5 * box.y + rng() * Math.max(0, box.y - height) * 0.18;
            const field = fbm3(u * 2.7 + 11.3, v * 2.7 - 4.1, seed * 0.013, 3, seed + 404);
            plans.push({
                u,
                v,
                baseY,
                width,
                height,
                rank: 0.55 * rng() + 0.45 * (1 - field),
                life: rng(),
                yaw: rng() * Math.PI * 2,
                seed: (seed * 31 + plans.length * 977 + 1) | 0,
                budget: 0,
            });
        }
    }
    if (plans.length === 0) {
        plans.push({ u: 0, v: 0, baseY: -0.5 * box.y, width: W, height: Math.min(W * shape.aspect, box.y), rank: 0, life: rng(), yaw: 0, seed: seed | 0, budget: 0 });
    }
    // Uniform ranks so coverage maps linearly onto the share of clouds that are out.
    const order = plans.map((p, i) => [p.rank, i] as const).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    order.forEach(([, i], k) => {
        plans[i].rank = (k + 0.5) / plans.length;
    });

    // ── 2. budget per cloud, by volume ───────────────────────────────────────────────────
    const volumes = plans.map((p) => p.width * p.width * p.height);
    const totalVolume = volumes.reduce((a, b) => a + b, 0) || 1;
    const minPerCloud = shape.kind === 'cirrus' ? 40 : 30;
    let assigned = 0;
    for (let i = 0; i < plans.length; i++) {
        plans[i].budget = Math.max(minPerCloud, Math.floor((budget * volumes[i]) / totalVolume));
        assigned += plans[i].budget;
    }
    if (assigned > budget) {
        const k = budget / assigned;
        for (const p of plans) p.budget = Math.max(8, Math.floor(p.budget * k));
    }

    // ── 3. splats ────────────────────────────────────────────────────────────────────────
    const capacity = plans.reduce((a, p) => a + p.budget, 0);
    const anchor = new Float32Array(capacity * 4);
    const offset = new Float32Array(capacity * 4);
    const rotation = new Float32Array(capacity * 4);
    const shapeOut = new Float32Array(capacity * 4);
    const extra = new Float32Array(capacity * 4);
    let count = 0;
    let side = 0;
    let top = -Infinity;
    let bottom = Infinity;
    const emit = (plan: CloudPlan, lx: number, ly: number, lz: number, sigma: number, ax: number, ay: number, az: number, qx: number, qy: number, qz: number, qw: number, alpha: number, h: number, rim: number, crease = 1) => {
        if (count >= capacity) return;
        // cloud-local → layer-local (rotate about Y by the cloud's yaw)
        const c = Math.cos(plan.yaw);
        const s = Math.sin(plan.yaw);
        const ox = lx * c + lz * s;
        const oz = -lx * s + lz * c;
        const i4 = count * 4;
        anchor[i4] = plan.u;
        anchor[i4 + 1] = plan.baseY / box.y;
        anchor[i4 + 2] = plan.v;
        anchor[i4 + 3] = plan.rank;
        offset[i4] = ox;
        offset[i4 + 1] = ly;
        offset[i4 + 2] = oz;
        offset[i4 + 3] = sigma;
        // the cloud's yaw, then the splat's own rotation
        const hy = plan.yaw * 0.5;
        const yq = { x: 0, y: Math.sin(hy), z: 0, w: Math.cos(hy) };
        rotation[i4] = yq.w * qx + yq.x * qw + yq.y * qz - yq.z * qy;
        rotation[i4 + 1] = yq.w * qy - yq.x * qz + yq.y * qw + yq.z * qx;
        rotation[i4 + 2] = yq.w * qz + yq.x * qy - yq.y * qx + yq.z * qw;
        rotation[i4 + 3] = yq.w * qw - yq.x * qx - yq.y * qy - yq.z * qz;
        shapeOut[i4] = ax;
        shapeOut[i4 + 1] = ay;
        shapeOut[i4 + 2] = az;
        shapeOut[i4 + 3] = Math.min(CLOUD_MAX_SPLAT_OPACITY, alpha);
        extra[i4] = h;
        extra[i4 + 1] = rim;
        extra[i4 + 2] = crease;
        extra[i4 + 3] = plan.life;
        const reach = 2.5 * sigma * Math.max(ax, ay, az);
        side = Math.max(side, Math.hypot(ox, oz) + reach);
        top = Math.max(top, plan.baseY + ly + reach);
        bottom = Math.min(bottom, plan.baseY + ly - reach);
        count++;
    };

    for (const plan of plans) {
        if (shape.kind === 'cirrus') buildCirrus(plan, look, emit);
        else buildVolume(plan, look, emit);
    }

    // ── 4. extinction grid + shadow map ──────────────────────────────────────────────────
    const geometryBase = {
        count,
        anchor: anchor.subarray(0, count * 4),
        offset: offset.subarray(0, count * 4),
        rotation: rotation.subarray(0, count * 4),
        shape: shapeOut.subarray(0, count * 4),
        extra: extra.subarray(0, count * 4),
    };
    if (!Number.isFinite(top)) {
        top = box.y * 0.5;
        bottom = -box.y * 0.5;
    }
    const grid = buildExtinctionGrid(geometryBase, box, W, bottom, top);
    return {
        ...geometryBase,
        ...grid,
        genBox: { ...box },
        extent: { side, top, bottom },
        clouds: plans.length,
    };
}

type Emit = (plan: CloudPlan, lx: number, ly: number, lz: number, sigma: number, ax: number, ay: number, az: number, qx: number, qy: number, qz: number, qw: number, alpha: number, h: number, rim: number, crease?: number) => void;

/** Quaternion product a * b, as [x, y, z, w]. */
function mulQuat(a: readonly number[], b: readonly number[]): [number, number, number, number] {
    return [
        a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
        a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
        a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
        a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
    ];
}

/** A random rotation about Y with a small tilt about X and Z, as a quaternion [x, y, z, w]. */
function randomYawTilt(rng: () => number, tilt: number): [number, number, number, number] {
    const yaw = rng() * Math.PI * 2;
    const ax = (rng() - 0.5) * tilt;
    const az = (rng() - 0.5) * tilt;
    const qy = [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)];
    const qx = [Math.sin(ax / 2), 0, 0, Math.cos(ax / 2)];
    const qz = [0, 0, Math.sin(az / 2), Math.cos(az / 2)];
    return mulQuat(mulQuat(qy, qx), qz);
}

/** Puffs of a cumulus-family cloud, in cloud-local space (base centre at the origin). */
function cumulusPuffs(plan: CloudPlan, look: CloudLook, rng: () => number): Puff[] {
    const s = look.shape;
    const w = plan.width;
    const H = plan.height;
    const puffs: Puff[] = [];
    const storm = s.kind === 'cumulonimbus';
    // base row: puffs around an ellipse, their bottoms near the condensation level
    const baseCount = 3 + Math.floor(rng() * 3) + (storm ? 1 : 0);
    const ex = w * 0.3;
    const ez = w * lerp(0.16, 0.26, rng());
    const baseR = Math.min(w * lerp(0.2, 0.27, rng()), H * 0.7);
    for (let i = 0; i < baseCount; i++) {
        const a = (i / baseCount) * Math.PI * 2 + rng() * 0.8;
        const r = baseR * lerp(0.75, 1.15, rng());
        puffs.push({ x: Math.cos(a) * ex * lerp(0.4, 1, rng()), y: r * 0.62, z: Math.sin(a) * ez * lerp(0.4, 1, rng()), r, sy: 0.9 });
    }
    // a central dome
    const coreR = Math.min(w * 0.3, H * 0.62);
    puffs.push({ x: (rng() - 0.5) * w * 0.1, y: Math.min(H * 0.42, coreR * 0.95), z: (rng() - 0.5) * w * 0.08, r: coreR, sy: 1 });
    if (storm) {
        // a rising column (the updraft) to most of the height, leaning a little with the shear,
        // with bulging turrets around it at every level
        const levels = Math.max(3, Math.round((H * 0.84 - coreR) / (coreR * 0.55)));
        const leanDir = rng() * Math.PI * 2;
        const lean = lerp(0.05, 0.22, rng());
        for (let l = 1; l <= levels; l++) {
            const t = l / levels;
            const y = coreR + t * (H * 0.84 - coreR);
            const r = coreR * lerp(0.95, 0.62, t) * lerp(0.9, 1.1, rng());
            const cx = Math.cos(leanDir) * lean * y + (rng() - 0.5) * r * 0.25;
            const cz = Math.sin(leanDir) * lean * y + (rng() - 0.5) * r * 0.25;
            puffs.push({ x: cx, y, z: cz, r, sy: 1 });
            const turrets = 2 + Math.floor(rng() * 3);
            for (let b = 0; b < turrets; b++) {
                const a = rng() * Math.PI * 2;
                const rb = r * lerp(0.45, 0.72, rng());
                puffs.push({ x: cx + Math.cos(a) * r * 0.72, y: y + (rng() - 0.3) * r * 0.5, z: cz + Math.sin(a) * r * 0.72, r: rb, sy: 1 });
            }
        }
    }
    // billow generations climbing the upper hemisphere of their parents
    let parents = puffs.slice();
    for (let g = 0; g < s.billows; g++) {
        const next: Puff[] = [];
        for (const p of parents) {
            const children = 1 + Math.floor(rng() * (storm ? 3 : 2.6));
            for (let k = 0; k < children; k++) {
                const az = rng() * Math.PI * 2;
                // elevation: towers climb (tower → 1), fair weather spreads (tower → 0)
                const elev = lerp(lerp(0.15, 0.45, s.tower), lerp(0.7, 1.35, s.tower), rng());
                const dx = Math.cos(az) * Math.cos(elev);
                const dy = Math.sin(elev);
                const dz = Math.sin(az) * Math.cos(elev);
                const r = p.r * lerp(0.48, storm ? 0.78 : 0.68, rng());
                const c = { x: p.x + dx * p.r * 0.72, y: p.y + dy * p.r * 0.72 * p.sy, z: p.z + dz * p.r * 0.72, r, sy: 1 };
                // keep tops under the cloud's height
                if (c.y + c.r > H * (storm ? 1.05 : 1.08)) continue;
                next.push(c);
            }
        }
        puffs.push(...next);
        parents = next;
        if (!parents.length) break;
    }
    // storms: an anvil spreading at the top of the tallest towers
    if (storm && plan.width > look.cloudScale * 0.95 && H > plan.width * 1.1) {
        const n = 6 + Math.floor(rng() * 4);
        const ay = H * lerp(0.88, 0.96, rng());
        const spread = w * lerp(0.55, 0.8, rng());
        const dir = rng() * Math.PI * 2;
        for (let i = 0; i < n; i++) {
            const t = i / (n - 1);
            const a = dir + (t - 0.5) * 1.3;
            const d = spread * (0.2 + 0.8 * t) * lerp(0.7, 1.1, rng());
            puffs.push({ x: Math.cos(a) * d * 0.7, y: ay, z: Math.sin(a) * d * 0.7, r: w * lerp(0.2, 0.3, rng()), sy: 0.35 });
        }
    }
    return puffs;
}

/** Cumulus, storm, stratus and fog clouds: rejection-sample a density field. */
function buildVolume(plan: CloudPlan, look: CloudLook, emit: Emit): void {
    const s = look.shape;
    const rng = createRng(plan.seed);
    const w = plan.width;
    const H = plan.height;
    const kind = s.kind;
    const slab = kind === 'stratus' || kind === 'fog';
    const puffs = slab ? [] : cumulusPuffs(plan, look, rng);

    // bounds of the field
    let minX = -w * 0.5, maxX = w * 0.5, minZ = -w * 0.5, maxZ = w * 0.5, minY = 0, maxY = H;
    if (!slab) {
        minX = minZ = Infinity; maxX = maxZ = -Infinity; minY = 0; maxY = 0;
        for (const p of puffs) {
            minX = Math.min(minX, p.x - p.r); maxX = Math.max(maxX, p.x + p.r);
            minZ = Math.min(minZ, p.z - p.r); maxZ = Math.max(maxZ, p.z + p.r);
            maxY = Math.max(maxY, p.y + p.r * p.sy);
        }
    }
    const nseed = plan.seed ^ 0x51ed27;
    const noiseScale = 1 / Math.max(w * (slab ? 0.16 : 0.13), 1e-4);
    const erosion = s.erosion;

    // Slab decks: rounded footprint, fBm top and bottom heights.
    const slabDensity = (x: number, y: number, z: number): number => {
        const rx = x / (w * 0.5);
        const rz = z / (w * 0.5);
        const r = Math.sqrt(rx * rx + rz * rz);
        const foot = 1 - smoothstep(0.55, 1.0, r + (fbm3(x * noiseScale * 0.5, 0, z * noiseScale * 0.5, 2, nseed) - 0.5) * 0.5);
        if (foot <= 0) return 0;
        const n = smoothstep(0.2, 0.8, fbm3(x * noiseScale * 0.35, 3.1, z * noiseScale * 0.35, 3, nseed + 9));
        const topY = H * (kind === 'fog' ? lerp(0.3, 1.0, n) : lerp(0.35, 1.0, n));
        const botY = kind === 'fog' ? 0 : H * 0.25 * fbm3(x * noiseScale * 0.4, 7.7, z * noiseScale * 0.4, 2, nseed + 3);
        if (y < botY || y > topY) return 0;
        // fog: even through its lower half, thinning to a soft top
        const vy = kind === 'fog'
            ? 1 - smoothstep(topY * 0.45, topY, y)
            : smoothstep(botY, botY + (topY - botY) * 0.3, y) * (1 - smoothstep(topY - (topY - botY) * 0.45, topY, y));
        return foot * vy;
    };
    // Cumulus: union of soft puffs on a flat base.
    const puffDensity = (x: number, y: number, z: number): number => {
        let d = 0;
        for (let i = 0; i < puffs.length; i++) {
            const p = puffs[i];
            const dx = x - p.x;
            const dy = (y - p.y) / p.sy;
            const dz = z - p.z;
            const q = (dx * dx + dy * dy + dz * dz) / (p.r * p.r);
            if (q >= 1) continue;
            const f = 1 - smoothstep(0.3, 1.0, Math.sqrt(q));
            if (f > d) d = f;
        }
        // flat base: the condensation level
        return d * smoothstep(0, H * 0.1, y);
    };
    const density = (x: number, y: number, z: number): number => {
        const base = slab ? slabDensity(x, y, z) : puffDensity(x, y, z);
        if (base <= 0) return 0;
        // billowy erosion of the rim (remap, as Nubis erodes its base shape with Worley noise)
        const n = billow3(x * noiseScale, y * noiseScale * 1.4, z * noiseScale, 3, nseed + 77);
        const e = erosion * (1 - n);
        return clamp((base - e) / Math.max(1e-3, 1 - e), 0, 1);
    };

    // Splats concentrate at the cloud's surface, where its shape and light are seen; the hidden
    // core gets fewer, larger ones. acceptance(d) is the sampling density for field value d.
    // (fog is seen through, not as a surface: it fills its volume evenly)
    const acceptance = kind === 'fog'
        ? (d: number) => smoothstep(0.02, 0.3, d)
        : (d: number) => smoothstep(0.02, 0.3, d) * (1 - 0.6 * smoothstep(0.5, 1.0, d));
    // Estimate the filled volume to size the splats from their spacing.
    const vx = maxX - minX, vy = maxY - minY, vz = maxZ - minZ;
    const boundsVolume = Math.max(1e-6, vx * vy * vz);
    let filled = 0;
    const probes = 600;
    for (let i = 0; i < probes; i++) {
        const d = density(minX + rng() * vx, minY + rng() * vy, minZ + rng() * vz);
        filled += acceptance(d);
    }
    const fillRatio = Math.max(0.02, filled / probes);
    const target = plan.budget;
    const spacing = Math.cbrt((boundsVolume * fillRatio) / Math.max(1, target));
    const sigma0 = spacing * 0.58 * s.overlap;

    let made = 0;
    const maxTrials = target * 18;
    for (let trial = 0; trial < maxTrials && made < target; trial++) {
        const x = minX + rng() * vx;
        const y = minY + rng() * vy;
        const z = minZ + rng() * vz;
        const d = density(x, y, z);
        if (d <= 0.01) continue;
        if (rng() > acceptance(d)) continue;
        const h = clamp((y - minY) / Math.max(1e-6, maxY - minY), 0, 1);
        const rim = clamp(1 - smoothstep(0.04, 0.85, d) + (rng() - 0.5) * 0.15, 0, 1);
        // small, crisp splats at the surface; the sparser core splats are larger to stay filled
        const sigma = sigma0 * lerp(0.48, 1.5, smoothstep(0.05, 0.95, d)) * lerp(0.85, 1.15, rng());
        // flatter splats at the base of cumulus (the flat condensation level), slabs flat throughout
        const squash = slab ? s.squash * lerp(0.85, 1.15, rng()) : s.squash * lerp(0.62, 1.0, smoothstep(0.0, 0.25, h));
        const stretch = s.stretch * lerp(0.85, 1.15, rng());
        const [qx, qy, qz, qw] = randomYawTilt(rng, slab ? 0.25 : 0.6);
        // sunlit tops have crisp edges, bases and decks soft ones
        const crisp = slab ? 0.65 : lerp(0.55, 1.3, smoothstep(0.15, 0.7, h));
        const alpha = s.opacity * lerp(0.07 * crisp, 0.38, smoothstep(0.03, 0.7, d));
        // folds between billows sit in shadow, the lobes catch the light (cauliflower detail the
        // coarse lighting grid can't resolve)
        const crease = smoothstep(0.25, 0.85, billow3(x * noiseScale * 1.6, y * noiseScale * 2.0, z * noiseScale * 1.6, 2, nseed + 131));
        emit(plan, x, y, z, sigma, Math.sqrt(stretch), squash, 1 / Math.sqrt(stretch), qx, qy, qz, qw, alpha, h, rim, crease);
        made++;
    }
}

/**
 * Cirrus: a patch of fine, nearly parallel fibres streaming with the wind (each splat stretched
 * along its fibre), patchy along their length, hooking and drooping at the downwind end (mares'
 * tails), over a faint veil that holds the patch together.
 */
function buildCirrus(plan: CloudPlan, look: CloudLook, emit: Emit): void {
    const s = look.shape;
    const rng = createRng(plan.seed);
    const w = plan.width;
    // Fibres follow the wind: its scene heading in PlayCanvas XZ (z mirrored), undoing the cloud's yaw.
    const flow = -(look.windDirection * Math.PI) / 180 + plan.yaw;
    const fx = Math.cos(flow), fz = Math.sin(flow);
    const veilBudget = Math.floor(plan.budget * 0.05);
    const fibres = 10 + Math.floor(rng() * 14);
    const perFibre = Math.max(10, Math.floor((plan.budget - veilBudget) / fibres));
    const across = w * lerp(0.35, 0.6, rng());
    const fan = (rng() - 0.5) * 0.35; // fibres splay a little across the patch
    for (let k = 0; k < fibres; k++) {
        const u = (k + rng() * 0.8) / fibres - 0.5; // position across the patch
        const len = w * lerp(0.45, 1.15, rng()) * (1 - Math.abs(u) * 0.6);
        let x = -fz * u * across - fx * len * 0.5 + (rng() - 0.5) * w * 0.15;
        let z = fx * u * across - fz * len * 0.5 + (rng() - 0.5) * w * 0.15;
        const y0 = (rng() - 0.5) * plan.height * 0.6;
        let heading = flow + fan * u + (rng() - 0.5) * 0.12;
        const curl = ((rng() - 0.5) * 0.9) / len;
        const hook = rng() < 0.55;
        const hookSide = rng() < 0.5 ? -1 : 1;
        const step = len / perFibre;
        const thickness = w * lerp(0.003, 0.01, rng());
        const sigma = Math.max(step * 1.5, thickness * 1.2) * s.overlap * 0.5;
        const fseed = plan.seed + k * 7;
        for (let i = 0; i < perFibre; i++) {
            const t = i / (perFibre - 1);
            const hooking = hook && t > 0.72 ? (t - 0.72) / 0.28 : 0;
            heading += curl * step + hookSide * hooking * hooking * 0.06;
            x += Math.cos(heading) * step;
            z += Math.sin(heading) * step;
            const lateral = gaussian(rng) * thickness;
            const lx = x - Math.sin(heading) * lateral;
            const lz = z + Math.cos(heading) * lateral;
            // the hook droops (fall streaks of ice)
            const ly = y0 + gaussian(rng) * thickness * 0.4 - hooking * hooking * plan.height * 0.6;
            // patchy along the fibre
            const patch = smoothstep(0.3, 0.75, fbm3((t * len) / (w * 0.12), k * 1.7, 0.5, 3, fseed));
            const profile = Math.pow(Math.sin(Math.PI * t), 0.45) * lerp(0.15, 1.0, patch);
            const alpha = s.opacity * 0.34 * profile;
            if (alpha < 0.012) continue;
            const half = heading * -0.5;
            const stretch = s.stretch * lerp(0.8, 1.6, rng());
            emit(plan, lx, ly, lz, sigma, stretch, s.squash, 0.6, 0, Math.sin(half), 0, Math.cos(half), alpha, 0.5, 1 - profile * 0.6, 1);
        }
    }
    // the veil: a few large, very faint splats over the patch
    for (let i = 0; i < veilBudget; i++) {
        const u = (rng() - 0.5) * across * 1.2;
        const v = (rng() - 0.5) * w;
        const lx = -fz * u + fx * v;
        const lz = fx * u + fz * v;
        const sigma = w * lerp(0.05, 0.09, rng());
        const half = flow * -0.5;
        emit(plan, lx, (rng() - 0.5) * plan.height * 0.4, lz, sigma, 2.2, 0.15, 1, 0, Math.sin(half), 0, Math.cos(half), s.opacity * 0.02, 0.5, 1, 1);
    }
}

/**
 * Splat every splat into a periodic (x, z) extinction grid over the layer, then sum columns into
 * the cloud-shadow map. Extinction from a splat with opacity a and sigma s peaks at
 * −ln(1 − a) / (s·√(2π)), so a ray through its centre loses what the splat's own opacity says.
 */
function buildExtinctionGrid(
    g: Pick<CloudGeometry, 'count' | 'anchor' | 'offset' | 'shape'>,
    box: { x: number; y: number; z: number },
    cloudScale: number,
    bottom: number,
    top: number,
): Pick<CloudGeometry, 'grid' | 'gridDims' | 'gridY' | 'sigmaMax' | 'shadow' | 'shadowDims' | 'tauMax' | 'cellSize'> {
    const cell = Math.max(cloudScale / 9, Math.max(box.x, box.z) / 128);
    const gx = clamp(Math.round(box.x / cell), 8, 128);
    const gz = clamp(Math.round(box.z / cell), 8, 128);
    const y0 = bottom - cell * 0.5;
    const y1 = top + cell * 0.5;
    const gy = clamp(Math.round((y1 - y0) / Math.min(cell, (y1 - y0) / 6)), 6, 48);
    const cx = box.x / gx;
    const cy = (y1 - y0) / gy;
    const cz = box.z / gz;
    const n = gx * gy * gz;
    const sigmaGrid = new Float32Array(n);
    const rankGrid = new Float32Array(n).fill(2);
    const sqrt2pi = Math.sqrt(2 * Math.PI);
    for (let i = 0; i < g.count; i++) {
        const i4 = i * 4;
        const px = (g.anchor[i4] + 0.5) * box.x + g.offset[i4];
        const py = g.anchor[i4 + 1] * box.y + g.offset[i4 + 1];
        const pz = (g.anchor[i4 + 2] + 0.5) * box.z + g.offset[i4 + 2];
        const rank = g.anchor[i4 + 3];
        const sigma = g.offset[i4 + 3];
        const sx = Math.max(1e-4, sigma * g.shape[i4]);
        const sy = Math.max(1e-4, sigma * g.shape[i4 + 1]);
        const sz = Math.max(1e-4, sigma * g.shape[i4 + 2]);
        const alpha = Math.min(0.95, g.shape[i4 + 3]);
        const iso = Math.cbrt(sx * sy * sz);
        const peak = -Math.log(1 - alpha) / (iso * sqrt2pi);
        // voxel centre coordinates in cell units
        const fx = px / cx - 0.5;
        const fy = (py - y0) / cy - 0.5;
        const fz = pz / cz - 0.5;
        const rx = Math.min(4, Math.ceil((2 * sx) / cx));
        const ry = Math.min(4, Math.ceil((2 * sy) / cy));
        const rz = Math.min(4, Math.ceil((2 * sz) / cz));
        const bx = Math.round(fx), by = Math.round(fy), bz = Math.round(fz);
        for (let dz = -rz; dz <= rz; dz++) {
            const vz = bz + dz;
            const wz = (vz - fz) * cz;
            const ez = (wz * wz) / (2 * sz * sz);
            const iz = ((vz % gz) + gz) % gz;
            for (let dy = -ry; dy <= ry; dy++) {
                const vy = by + dy;
                if (vy < 0 || vy >= gy) continue;
                const wy = (vy - fy) * cy;
                const ey = ez + (wy * wy) / (2 * sy * sy);
                if (ey > 4.5) continue;
                for (let dx = -rx; dx <= rx; dx++) {
                    const vx = bx + dx;
                    const wx = (vx - fx) * cx;
                    const e = ey + (wx * wx) / (2 * sx * sx);
                    if (e > 4.5) continue;
                    const ix = ((vx % gx) + gx) % gx;
                    const idx = ix + gx * (iz + gz * vy);
                    // the kernel integrates to the splat's extinction over the cell volume
                    sigmaGrid[idx] += peak * Math.exp(-e);
                    if (rank < rankGrid[idx]) rankGrid[idx] = rank;
                }
            }
        }
    }
    // sigmaMax: a high percentile (histogram), so one dense voxel doesn't flatten the rest
    let maxSigma = 0;
    let nonZero = 0;
    for (let i = 0; i < n; i++) {
        if (sigmaGrid[i] > 0) nonZero++;
        if (sigmaGrid[i] > maxSigma) maxSigma = sigmaGrid[i];
    }
    let sigmaMax = Math.max(1e-6, maxSigma);
    if (nonZero > 0) {
        const bins = new Uint32Array(1024);
        for (let i = 0; i < n; i++) if (sigmaGrid[i] > 0) bins[Math.min(1023, Math.floor((sigmaGrid[i] / maxSigma) * 1024))]++;
        const want = Math.floor(nonZero * 0.995);
        let acc = 0;
        for (let b = 0; b < 1024; b++) {
            acc += bins[b];
            if (acc >= want) {
                sigmaMax = Math.max(1e-6, ((b + 1) / 1024) * maxSigma);
                break;
            }
        }
    }
    const grid = new Uint8Array(n * 2);
    for (let i = 0; i < n; i++) {
        grid[i * 2] = Math.round(255 * Math.min(1, sigmaGrid[i] / sigmaMax));
        grid[i * 2 + 1] = Math.round(255 * Math.min(1, rankGrid[i]));
    }
    // column optical depth, blurred once (periodic)
    const tau = new Float32Array(gx * gz);
    const rankCol = new Float32Array(gx * gz).fill(2);
    for (let iz = 0; iz < gz; iz++) {
        for (let ix = 0; ix < gx; ix++) {
            let sum = 0;
            let r = 2;
            for (let iy = 0; iy < gy; iy++) {
                const idx = ix + gx * (iz + gz * iy);
                const v = sigmaGrid[idx];
                sum += v * cy;
                if (v > sigmaMax * 0.02 && rankGrid[idx] < r) r = rankGrid[idx];
            }
            tau[ix + gx * iz] = sum;
            rankCol[ix + gx * iz] = r;
        }
    }
    const blurred = new Float32Array(gx * gz);
    for (let iz = 0; iz < gz; iz++) {
        for (let ix = 0; ix < gx; ix++) {
            let sum = 0;
            let wsum = 0;
            for (let dz = -1; dz <= 1; dz++) {
                for (let dx = -1; dx <= 1; dx++) {
                    const w = dx === 0 && dz === 0 ? 4 : dx === 0 || dz === 0 ? 2 : 1;
                    sum += tau[((ix + dx + gx) % gx) + gx * ((iz + dz + gz) % gz)] * w;
                    wsum += w;
                }
            }
            blurred[ix + gx * iz] = sum / wsum;
        }
    }
    let tauMax = 1e-6;
    for (let i = 0; i < blurred.length; i++) tauMax = Math.max(tauMax, blurred[i]);
    const shadow = new Uint8Array(gx * gz * 2);
    for (let i = 0; i < gx * gz; i++) {
        shadow[i * 2] = Math.round(255 * Math.min(1, blurred[i] / tauMax));
        shadow[i * 2 + 1] = Math.round(255 * Math.min(1, rankCol[i]));
    }
    return {
        grid,
        gridDims: [gx, gy, gz],
        gridY: [y0, y1],
        sigmaMax,
        shadow,
        shadowDims: [gx, gz],
        tauMax,
        cellSize: Math.min(cx, cy, cz),
    };
}

/**
 * Layer-local centre of splat i for a wind drift (normalised, any value: it wraps) and box: the
 * same placement the shader computes, minus the small billowing offsets. Feeds the CPU sorter.
 */
export function cloudSplatCenter(
    g: Pick<CloudGeometry, 'anchor' | 'offset' | 'genBox'>,
    i: number,
    box: { x: number; y: number; z: number },
    driftU: number,
    driftV: number,
    curvature: number,
    out: Float32Array,
    o: number,
): void {
    // the clouds sit in the generated box (drifting and wrapping through it); the live box `box`
    // is a window into it whose floor they rest on (as the read code places them)
    const gb = g.genBox;
    const i4 = i * 4;
    const u = wrapHalf(g.anchor[i4] + driftU);
    const v = wrapHalf(g.anchor[i4 + 2] + driftV);
    const ax = u * gb.x;
    const az = v * gb.z;
    out[o] = ax + g.offset[i4];
    out[o + 1] = g.anchor[i4 + 1] * gb.y + 0.5 * (gb.y - box.y) + g.offset[i4 + 1] - curvature * (ax * ax + az * az);
    out[o + 2] = az + g.offset[i4 + 2];
}

/** Wrap to [−0.5, 0.5), matching the shader's fract(x + 0.5) − 0.5. */
export function wrapHalf(x: number): number {
    const y = x + 0.5;
    return y - Math.floor(y) - 0.5;
}
